// ============================================================================
// PublicationCard — carte de publication du fil social (MASTER-GEST-E1A-ACCUEIL)
// ============================================================================
// Carte de fil : avatar auteur + nom + temps relatif + catégorie (texte discret) ·
// titre text-body semibold · corps + « Lire la suite » (lien brand — dérouler du
// CONTENU est du wayfinding) · pied = barre de réactions emoji + « Vu par N » +
// « N commentaires ». Barre de réactions INTERNE (décision E1a — la promotion
// GsReactionBar standalone est un chore ultérieur si réutilisée) et LECTURE
// seule (compteurs affichés ; l'interaction réagir/commenter = E1b).
// Data-driven, i18n par props (JAMAIS de t() dans @gs/ui) — l'appelant fournit
// les libellés déjà traduits/formatés (temps relatif, « Vu par N », etc.).
// Tokens slate-*/brand-* + échelle nommée uniquement (règle #28) — zéro hex.

import { useLayoutEffect, useRef, useState } from 'react';
import { GsAvatar } from './GsAvatar';

export interface PublicationCardReaction {
  /** Clé stable de la réaction (like | love | celebrate…). */
  key: string;
  /** Emoji affiché (👍 ❤️ 🎉). */
  emoji: string;
  /** Libellé traduit (« J'aime »). Omis en rendu compact si vide. */
  label: string;
  count: number;
}

export interface PublicationCardProps {
  authorName: string;
  authorPhotoUrl?: string | null;
  /** Temps relatif déjà formaté (« il y a 14 jours »). */
  postedAtLabel: string;
  /** Catégorie déjà traduite (« Contrats ») — texte discret, pas un badge. */
  categoryLabel: string;
  title: string;
  body: string;
  /** Libellé « Lire la suite » (obligatoire — apparaît si le corps déborde). */
  readMoreLabel: string;
  /** Libellé de repli optionnel (« Réduire ») — sans lui, pas de repli après expansion. */
  readLessLabel?: string;
  reactions?: PublicationCardReaction[];
  /** « Vu par 10 » déjà formaté — masqué si absent. */
  viewsLabel?: string;
  /** « 1 commentaire » déjà formaté — masqué si absent. */
  commentsLabel?: string;
  className?: string;
}

/** Carte de publication du fil — lecture seule (E1a), réactions affichées. */
export function PublicationCard({
  authorName,
  authorPhotoUrl,
  postedAtLabel,
  categoryLabel,
  title,
  body,
  readMoreLabel,
  readLessLabel,
  reactions = [],
  viewsLabel,
  commentsLabel,
  className = '',
}: PublicationCardProps) {
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Le lien « Lire la suite » n'apparaît que si le corps clampé déborde réellement.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [body, expanded]);

  const hasFoot = reactions.length > 0 || viewsLabel || commentsLabel;

  return (
    <article
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 ${className}`.trim()}
    >
      <header className="mb-2.5 flex items-center gap-2.5">
        <GsAvatar name={authorName} photoUrl={authorPhotoUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-field font-semibold leading-tight text-slate-700 dark:text-slate-200">
            {authorName}
          </p>
          <p className="text-meta text-slate-400 dark:text-slate-500">{postedAtLabel}</p>
        </div>
        <span className="shrink-0 text-meta font-medium text-slate-400 dark:text-slate-500">
          {categoryLabel}
        </span>
      </header>

      <h3 className="mb-1.5 text-body font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
      <p
        ref={bodyRef}
        className={`whitespace-pre-line text-body leading-relaxed text-slate-600 dark:text-slate-300 ${expanded ? '' : 'line-clamp-3'}`.trim()}
      >
        {body}
      </p>
      {(overflowing || (expanded && readLessLabel)) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 touch-manipulation text-body font-medium text-brand-600 transition-colors hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
        >
          {expanded ? readLessLabel : readMoreLabel}
        </button>
      )}

      {hasFoot && (
        <footer className="mt-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-slate-100 pt-3 dark:border-slate-700/60">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {reactions.map((r) => (
              <span
                key={r.key}
                className="inline-flex items-center gap-1.5 text-field text-slate-600 dark:text-slate-300"
              >
                <span aria-hidden>{r.emoji}</span>
                {r.label && <span>{r.label}</span>}
                {r.count > 0 && (
                  <span className="text-meta tabular-nums text-slate-400 dark:text-slate-500">{r.count}</span>
                )}
              </span>
            ))}
          </div>
          {(viewsLabel || commentsLabel) && (
            <div className="flex items-center gap-3.5 text-meta text-slate-400 dark:text-slate-500">
              {viewsLabel && <span>{viewsLabel}</span>}
              {commentsLabel && <span>{commentsLabel}</span>}
            </div>
          )}
        </footer>
      )}
    </article>
  );
}
